import type { DashboardOptions } from '../model';

export const formatNumber = (value: number, fractionDigits = 1): string =>
  value.toLocaleString('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  });

/**
 * Formats a dashboard metric value for display. Shared across all dashboard pages
 * (service users, storage overview, transport overview).
 *
 * The raw value is already in the displayed unit — do NOT divide weight by 1000
 * (that was the `342406` bug).
 *
 * `fractionDigits` widens the precision where a single decimal would swallow the
 * number — legacy prints pie slices to two places for exactly that reason.
 *
 * @example
 * formatValue(1727.9445836679997, 'W'); // '1,727.9 mt'
 * formatValue(1658.3585309499997, 'W'); // '1,658.4 mt'
 * formatValue(0.1247, 'W', 2); // '0.12 mt'
 */
export const formatValue = (value: number, metric?: DashboardOptions['metric'], fractionDigits?: number): string => {
  const magnitude = Math.abs(value);

  switch (metric) {
    case 'W':
      return `${formatNumber(magnitude, fractionDigits)} mt`;
    case 'V':
      return `${formatNumber(magnitude, fractionDigits)} m³`;
    case 'VA':
      return `$${magnitude.toLocaleString('en-US')}`;
    default:
      return magnitude.toLocaleString('en-US');
  }
};
